// Pure RK4 field-line tracer. No THREE deps.
//
// sample(x, y, z) -> [bx, by, bz]  (see physics/sample.ts)
// dir is +1 (forward along field) or -1 (backward).
// The tracer steps with unit-normalised direction so |delta| ≈ h.

import { vecMag, vecNorm } from './sample';
import type { BoundsPredicate, MutableVec3, Vec3, VectorSampler } from './types';

type Pt = MutableVec3;

export function rk4Step(sample: VectorSampler, pos: Vec3, h: number, dir: number): Pt {
  const s = Math.sign(dir) || 1;
  const k1n = vecNorm(scale(sample(pos[0], pos[1], pos[2]), s));
  const p2 = add(pos, scale(k1n, 0.5 * h));
  const k2 = vecNorm(scale(sample(p2[0], p2[1], p2[2]), s));
  const p3 = add(pos, scale(k2, 0.5 * h));
  const k3 = vecNorm(scale(sample(p3[0], p3[1], p3[2]), s));
  const p4 = add(pos, scale(k3, h));
  const k4 = vecNorm(scale(sample(p4[0], p4[1], p4[2]), s));
  return [
    pos[0] + (h / 6) * k1n[0] + (h / 3) * k2[0] + (h / 3) * k3[0] + (h / 6) * k4[0],
    pos[1] + (h / 6) * k1n[1] + (h / 3) * k2[1] + (h / 3) * k3[1] + (h / 6) * k4[1],
    pos[2] + (h / 6) * k1n[2] + (h / 3) * k2[2] + (h / 3) * k3[2] + (h / 6) * k4[2],
  ];
}

export interface TraceDeps {
  sample: VectorSampler;
  isOutside: BoundsPredicate;
}

export function traceSync({ sample, isOutside }: TraceDeps, start: Vec3, h: number, maxSteps: number): Pt[] {
  if (isOutside(start[0], start[1], start[2])) return [];
  const forward: Pt[] = [];
  const backward: Pt[] = [];
  let p: Pt = [start[0], start[1], start[2]];
  for (let n = 0; n < maxSteps; n++) {
    if (isOutside(p[0], p[1], p[2])) break;
    if (vecMag(p) < 1.0) break; // inner exclusion sphere (Earth radius)
    forward.push([p[0], p[1], p[2]]);
    p = rk4Step(sample, p, h, +1);
  }
  p = [start[0], start[1], start[2]];
  for (let n = 0; n < maxSteps; n++) {
    if (isOutside(p[0], p[1], p[2])) break;
    if (vecMag(p) < 1.0) break;
    backward.push([p[0], p[1], p[2]]);
    p = rk4Step(sample, p, h, -1);
  }
  const combined = backward.reverse();
  if (combined.length && forward.length && eq(combined[combined.length - 1]!, forward[0]!)) {
    combined.pop();
  }
  for (const pt of forward) combined.push(pt);
  const eps = Math.max(1e-6, h * 1e-3);
  const filtered: Pt[] = [];
  for (let i = 0; i < combined.length; i++) {
    const c = combined[i]!;
    const last = filtered[filtered.length - 1];
    if (last === undefined || dist(last, c) > eps) filtered.push(c);
  }
  return filtered;
}

function add(a: Vec3, b: Vec3): Pt { return [a[0] + b[0], a[1] + b[1], a[2] + b[2]]; }
function scale(a: Vec3, s: number): Pt { return [a[0] * s, a[1] * s, a[2] * s]; }
function eq(a: Vec3, b: Vec3): boolean { return a[0] === b[0] && a[1] === b[1] && a[2] === b[2]; }
function dist(a: Vec3, b: Vec3): number {
  const dx = a[0] - b[0], dy = a[1] - b[1], dz = a[2] - b[2];
  return Math.sqrt(dx * dx + dy * dy + dz * dz);
}
